import tasksService from '../services/tasksService.js';
import completionStepService from '../services/completionStepService';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue.js';
import AnalyticsLogger from '../helpers/analytics/analyticsLogger';
import ReduxConstants from '../constants/ReduxConstants.js';
import DAO from './DAO.js';
import Metadata from '../helpers/metadata/Metadata';
import MobileFiles from '../helpers/MobileFiles';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import { RecordStates } from '../constants/ReduxConstants.js';
import _ from 'lodash';

// TODO: instead of using this same descriptor everywhere, we should modify on 
// a case by case basis
export const completion_criteria_root = {
  completion_criteria: {
    attachments: { conversions: {}, user: {}, additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'] }, 
    comments: {
      user: { additionalFields: ['profile_image'] },
    },
    event_histories: { activity: {}, activity_sub_type: {}, user_who_performed_action: {} },
    form_fields: {},
  },
};

export const completion_criteria_attributes = {
  completion_criteria: {},
};

//Discuss making more fine-grained calls and proper propagation

export default class CompletionCriteriaDAO extends DAO {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor(store) {
    super(store, 'completion_criterion');
    this.OfflineQueue = OfflineQueue;

    this.unsetCompletionCriterion = this.unsetCompletionCriterion.bind(this);
    this.clearCompletionCriteria = this.clearCompletionCriteria.bind(this);
    this.markCompletionCriteriaNeedsSync = this.markCompletionCriteriaNeedsSync.bind(this);
    this.markDirty = this.markDirty.bind(this);
    this.getCompletionCriterion = this.getCompletionCriterion.bind(this);
    this.maybeMarkDirty = this.maybeMarkDirty.bind(this);
    this.checkForPendingAttachments = this.checkForPendingAttachments.bind(this);
    this.getStatus = this.getStatus.bind(this);
    this.internalACUpdate = this.internalACUpdate.bind(this);
    this.setCompleted = this.setCompleted.bind(this);
    this.checkPORCollections = this.checkPORCollections.bind(this);
    this.addNormalizeHook('completion_criterion', this.checkForPendingAttachments);
  }

  unsetCompletionCriterion(ccId) {
    StoreItemProcessing.deleteItemInStore(this.itemType, ccId);
  }

  clearCompletionCriteria() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  markDirty(cc, dirtyState) {
    super.markDirty(cc, dirtyState);

    // Make/unmark parents

    if (cc.task_id) {
      const task = dataModel.tasksDAO.findItem(cc.task_id);
      if (task) {
        dataModel.tasksDAO.markDirty(task, dirtyState);
      }
    }
  }

  maybeMarkDirty(cc) {
    let isDirty = false;
    if (cc.attachments) {
      for (const attachmentId of cc.attachments) {
        const attachment = (typeof attachmentId == 'number') ? dataModel.attachmentsDAO.findItem(attachmentId) : attachmentId;
        if (attachment && attachment._dirty) {
          isDirty = true;
          break;
        }
      }
    }
    this.markDirty(cc, isDirty);
  }

  internalACUpdate(completionCriterion) {
    dataModel.actionCableConnector.internalUpdate({ type: 'updateStep', step_id: completionCriterion.id }, completionCriterion.id);
    dataModel.tasksDAO.setStatus(completionCriterion.task_id);
  }

  setCompleted(id) {
    let completionCriterion = this.findItem(id);
    if (completionCriterion) {
      // completionCriterion.completed_at = new Date().toJSON()

      // completionCriterion.status = this.getStatus(completionCriterion)

      // completionCriterion.rejected_at = null
      this.setItemInStore(completionCriterion);
      this.internalACUpdate(completionCriterion);
    }
  }

  getCompletionCriterionDenormalized(ccId, options) {
    return this.mapToReturn(this.findItem(ccId), options);
  }

  getCompletionCriterion(ccId, options, prefetchCallback) {
    return this._getItem(ccId, completionStepService.getStep.bind(this, ccId, options || {}), options, null, null, prefetchCallback).then(async (cs) => {
      if (cs && cs.attachments && MobileFiles.available()) {
        let attachmentCount = cs.attachments.length;
        for (let maybeAttachment of cs.attachments) {
          let attachmentId;
          if (_.isObject(maybeAttachment)) {
            attachmentId = maybeAttachment.id;
          } else {
            attachmentId = maybeAttachment;
          }
          const attachment = dataModel.attachmentsDAO.findItem(attachmentId);
          if (attachment && attachment.upload && !(attachment.upload instanceof Blob) && attachment.upload.filename) {
            try {
              attachment.upload.filename = MobileFiles.fixFilename(attachment.upload.filename, attachment.upload.path);
              let blob = await MobileFiles.loadBlobFromFilename(attachment.upload.filename, attachment.upload.path);
              blob.name = attachment.upload.name;
              blob.filename = attachment.upload.filename;
              attachment.upload = blob;
              dataModel.attachmentsDAO.normalizeItem(attachment, { store: true });
              if (_.isObject(maybeAttachment)) {
                maybeAttachment.upload = blob;
              }
              attachmentCount--;
              if (attachmentCount == 0) {
                return cs;
              }
            } catch (error) {
              // TODO
            }
          }
          else {
            return cs;
          }
        }
      }
      return cs;
    });
      
  }

  async getCompletionCriterionEventCounts(ccId) {
    return completionStepService.getEventCounts(ccId).then(response => {
      if (response.error) {
        throw response.error;
      }
      else if (response.data) {
        return response.data;
      }
      else return response;
    });
  }

  markCompletionCriteriaNeedsSync() {
    const storeItemType = StoreItemProcessing.mapToStoreName('completion_criteria');
    let stateTree = this.stateTree;
    const storeForItemType = stateTree[StoreItemProcessing.mapToReducer(storeItemType)];
    for (let cs_id in storeForItemType) {
      if (cs_id) {
        let cs = this.findItem(cs_id);
        if (cs) {
          cs._recordState = RecordStates.NEEDS_SYNC;
          this.setItemInStore(cs);
        }
      }
    }
  }

  /* CC CRUD FUNCTIONS */
  async handleResponse(dontQueue, denormalizedCC, response, activity, completion, reason, upload, ignoreAnalytics, errorMessage, eventMetadatum, options) {
    if (response.error && !response.offline) {
      dataModel.displayHTTPError(errorMessage, response.error, false);
      this.markError(denormalizedCC);
      this.throwItemError();
    } else {
      if (response.offline) {
        
        if (dontQueue) {
          return response;
        }
        this.handleOfflineResponse(denormalizedCC, activity, completion, reason, upload, ignoreAnalytics, eventMetadatum, options);

        // get the whole denormalized item to pick up any rejection comments
        denormalizedCC = await this.getCompletionCriterion(denormalizedCC.id, { recordDescriptor: completion_criteria_root });
        if (activity == 'complete') {
          if (_.isBoolean(completion)) {
            denormalizedCC.completed_at = new Date().toJSON();
          } else {
            denormalizedCC.response = completion;
          }
        }
        else if (activity == 'approve') {
          denormalizedCC.approved_at = new Date().toJSON();
        }
        else if (activity == 'review') {
          denormalizedCC.reviewed_at = new Date().toJSON();
        }
        else if (activity == 'reject' || activity == 'revert') {
          if (activity == 'reject') {
            denormalizedCC.rejected_at = new Date().toJSON();
          }
          denormalizedCC.completed_at = null;
          denormalizedCC.approved_at = null;
          denormalizedCC.reviewed_at = null;
        }
        if (!options) {
          options = {};
        }
        options.store = true;
        StoreItemProcessing.normalizeItem(this.itemType, denormalizedCC, options);
        return denormalizedCC;
      } else {
        if (!ignoreAnalytics) {
          //TODO: move to components
          const requestedFileType = denormalizedCC.requested_file_type;
          this.handleAnalytics(activity, denormalizedCC.completion_type, completion, reason, requestedFileType);
        }
        this.markDirty(denormalizedCC, false);
        StoreItemProcessing.normalizeAndSyncToStore(this.itemType, response);
        return this.mapToReturn(this.findItem(denormalizedCC.id), options);
      }
    }
  }

  handleOfflineResponse(denormalizedCC, activity, completion, reason, upload, ignoreAnalytics, eventMetadatum, options) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    this.markDirty(denormalizedCC, true);
   
    this.OfflineQueue.addQueueItem(this.itemType, activity, { cc: denormalizedCC, completion, reason, upload, ignoreAnalytics, eventMetadatum, options });

    return denormalizedCC;
  }

  handleAnalytics(activity, completion_type, completion, reason, fileExtension) {
    if (!this.analyticsLogger) {
      const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
      this.analyticsLogger = new AnalyticsLogger({
        currentRole,
        isMobile: !!window.cordova,
      });
    }
    switch (activity) {
    case 'upload':
      this.analyticsLogger.logUploadEvent(fileExtension);
      break;
    case 'complete':
      if (completion_type == 'boolean') {
        if (completion) {
          this.analyticsLogger.logCheckoffEvent();
        }
      }
      else if (completion_type == 'inspection') {
        this.analyticsLogger.logInspectionEvent(reason);
      }
      break;
    case 'reject':
      this.analyticsLogger.logRejectionEvent();
      break;
    case 'review':
      this.analyticsLogger.logReviewEvent();
      break;
    case 'approve':
      this.analyticsLogger.logApprovalEvent();
      break;
    case 'notApplicable':
      switch (completion_type) {
      case 'boolean':
        this.analyticsLogger.logNotApplicable('CheckoffNotApplicable', fileExtension);
        break;
      case 'file_upload':
        this.analyticsLogger.logNotApplicable('UploadNotApplicable', fileExtension);
        break;
      case 'form':
        this.analyticsLogger.logNotApplicable('FormNotApplicable', fileExtension);
        break;
      case 'inspection':
        this.analyticsLogger.logNotApplicable('InspectionNotApplicable', fileExtension);
        break;
      default:
        break;
      }
      break;
    case 'undoNotApplicable':
      this.analyticsLogger.logNotApplicable('N/AUndo', fileExtension);
      break;
  
    default:
      break;
    }
  }

  /**
  * @param  {object <task>} task - denormalized task to create
  * @param  {object <object>} options.recordDescriptor - used to format returned task
  * 
  * @returns {object<Promise<task>>} task - denormalized task
  */
  createCompletionCriterion(completionStep, eventMetadatum, options) {
    return tasksService.createCompletionCriterion(completionStep, eventMetadatum, options).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Creating a completionStep', response.error, false);
        this.throwItemError('Error creating a completionStep', response);
      }
      else {
        this.markDirty(completionStep, false);
        this.normalizeAndSyncToStore(response);
        return this.mapToReturn(this.findItem(response.data.id), options);
      }
    })
      .catch((error) => {
        this.throwItemError();
      });
  }

  /*
  * @param  {object <cc>} cc - denormalized cc to update
  * @param  {object <object>} options.recordDescriptor - used to format returned cc
  * 
  * @returns {object<Promise<cc>>} cc - denormalized cc
  */
  updateCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      const normalizedCC = this.normalizeItem(completionCriterion, { store: true });
      // restore form fields since these are handled by the update
      normalizedCC.form_fields = completionCriterion.form_fields;
      if (normalizedCC.completion_type != 'form') {
        normalizedCC.form_fields = null;
      }

      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        //TODO: return resolved cc instead of throwing error?
        return resolve(this.handleOfflineResponse(completionCriterion, 'update', null, null, null, ignoreAnalytics, eventMetadatum, options));
      }
      else {
        return resolve(tasksService.updateCompletionCriterion(completionCriterion, eventMetadatum, options).then(response => {
          return this.handleResponse(dontQueue, completionCriterion, response, 'update', null, null, null, ignoreAnalytics, 'Updating a completion step', eventMetadatum, options);
        })
          .catch((error) => {
            this.markError(completionCriterion);
            this.throwItemError();
          })
        );
      }
    });
  }

  async copyCompletionCriterion(completionCriterion, changedData) {
    return tasksService.copyCompletionCriterion(completionCriterion, changedData).then(response => {
      return response;
    })
    .catch((error) => {
      this.throwItemError(error);
    });
  }

  /**
  * @param  {number} ccId - cc record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  deleteCompletionCriterion(ccId, dontQueue, eventMetadatum, options) {
    //TODO: action cable
    let completionCriterion = this.findItem(ccId);
    if (completionCriterion) {
      this.unsetCompletionCriterion(ccId);
    }
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }

    if (dataModel.isOffline) {
      this.OfflineQueue.addQueueItem(this.itemType, 'delete', { ccId, eventMetadatum });
      this.throwItemError();
    }
    else {
      return tasksService.deleteCompletionCriterion(ccId, eventMetadatum).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('Deleting a Completion Step', response.error, false);
          if (completionCriterion) {
            this.markError(completionCriterion);
            StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(completionCriterion));
          }
          this.throwItemError();
        }
        else if (response.offline) {
          this.OfflineQueue.addQueueItem(this.itemType, 'delete', { ccId, eventMetadatum });
          this.throwItemError();
        }
        else {
          if (completionCriterion && completionCriterion.task_id) {
            const task = dataModel.tasksDAO.findItem(completionCriterion.task_id);
            dataModel.tasksDAO.markDirty(task, false);
          }
          dataModel.displayInfoErrorMessage('The Completion Step was successfully deleted.', 4000, 1);
          return this.mapToReturn(completionCriterion);
        }
      });
    }
  }

  reorderCompletionCriteria(task, reorderList) {
    return new Promise((resolve) => {
      dataModel.tasksDAO.normalizeItem(task, { store: true });
      dataModel.actionCableConnector.internalUpdate({ type: 'updateTask', task_id: task.id }, task.id);
    
      if (reorderList && reorderList.length > 0) {
        let reorderPairs = reorderList.map(cc => { return { id: cc.id, order: cc.order }; });
        tasksService.reorderCompletionCriterion(task.id, reorderPairs).then(response => {
        });
        resolve(reorderList);
      }
      else {
        resolve(null);
      }
    });
  }

  getStatus(completionCriterion, task) {
    task = task ? task : dataModel.tasksDAO.findItem(completionCriterion.task_id);
    if (task) {
      if (completionCriterion.approved_at) {
        return 'approved';
      }
      else if (completionCriterion.reviewed_at) {
        return task.has_approver ? 'pending_approval' : 'complete';
      }
      else if (completionCriterion.completed_at) {
        return task.has_reviewer ? 'pending_review' : task.has_approver ? 'pending_approval' : 'complete';
      }
      else if (completionCriterion.rejected_at) {
        return 'rejected';
      }
      else {
        return 'not_started';
      }
    }
  }

  completeCompletionCriterion(completionCriterion, completion, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      completionCriterion.completed_at = completion ? new Date().toJSON() : null;

      completionCriterion.status = this.getStatus(completionCriterion);

      completionCriterion.rejected_at = null;
      const normalizedCC = this.normalizeItem(completionCriterion, { store: true });
      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'complete', completion, null, null, ignoreAnalytics, eventMetadatum, options));
      } else {
        return resolve(tasksService.completeCompletionCriterion(normalizedCC, completion, eventMetadatum, options).then(response => {
          return this.handleResponse(dontQueue, completionCriterion, response, 'complete', completion, null, null, ignoreAnalytics, 'Updating a completion step', eventMetadatum, options);
        }).catch((error) => {
          this.markError(completionCriterion);
          this.throwItemError();
        }));
      }
    });
  }

  markNotApplicable(completionCriterion, dontQueue, eventMetadatum) {
    return new Promise((resolve) => {
      completionCriterion.not_applicable = true;
      completionCriterion.rejected_at = null;
      delete completionCriterion.form_fields;
      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      completionCriterion.not_applicable = true;
      completionCriterion.completed_at = new Date().toJSON();
      completionCriterion.status = this.getStatus(completionCriterion);

      this.handleAnalytics('notApplicable', completionCriterion.completion_type, null, completionCriterion.requested_file_type);
      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'mark_not_applicable', null, null, null, false, eventMetadatum, { recordDescriptor: completion_criteria_attributes }));
      } else {
        return resolve(this.updateCompletionCriterion(completionCriterion, true, dontQueue, eventMetadatum, { recordDescriptor: completion_criteria_attributes }));
      }
    });
  }

  unmarkNotApplicable(completionCriterion, dontQueue, eventMetadatum) {
    return new Promise((resolve) => {
      completionCriterion.not_applicable = null;
      completionCriterion.status = 'not_started';
      completionCriterion.completed_at = null;
      completionCriterion.reviewed_at = null;
      completionCriterion.approved_at = null;
      delete completionCriterion.form_fields;
      delete completionCriterion.form_fields;
      this.handleAnalytics('undoNotApplicable', completionCriterion.completion_type, null, completionCriterion.requested_file_type);

      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'unmark_not_applicable', null, null, null, false, eventMetadatum, { recordDescriptor: completion_criteria_attributes }));
      } else {
        return resolve(this.updateCompletionCriterion(completionCriterion, true, dontQueue, eventMetadatum, { recordDescriptor: completion_criteria_attributes }));
      }
    });
  }

  rejectCompletionCriterion(completionCriterion, reason, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      completionCriterion.rejected_at = new Date().toJSON();
      completionCriterion.completed_at = null;
      completionCriterion.reviewed_at = null;
      completionCriterion.approved_at = null;
      completionCriterion.status = 'rejected';
    
      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'reject', null, reason, null, null, eventMetadatum, null));
      } else {
        return resolve(tasksService.rejectCompletionCriterion(completionCriterion, reason, eventMetadatum, options).then(response => {
          
          dataModel.commentsDAO.createComment({
            body: reason,
            caption: 'REJECTED',
            commentable_type: 'CompletionCriterion',
            commentable_id: completionCriterion.id,
            user_id: dataModel.usersDAO.currentUser.id,
          }, false, null, { recordDescriptor: { comments: {} } });
          
          return this.handleResponse(dontQueue, completionCriterion, response, 'reject', null, reason, null, ignoreAnalytics, 'Rejecting a completion step', eventMetadatum, options);
        })
          .catch((error) => {
            this.markError(completionCriterion);
            this.throwItemError();
          }));
      }
    });
  }

  reviewCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      completionCriterion.reviewed_at = new Date().toJSON();
      completionCriterion.status = this.getStatus(completionCriterion);

      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'review', null, null, null, ignoreAnalytics, eventMetadatum, options));
      } else {
        return resolve(tasksService.reviewCompletionCriterion(completionCriterion, eventMetadatum, options).then(response => {
          return this.handleResponse(dontQueue, completionCriterion, response, 'review', null, null, null, ignoreAnalytics, 'Reviewing a completion step', eventMetadatum, options);
        }));
      }
    });
  }

  approveCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      completionCriterion.approved_at = new Date().toJSON();
      completionCriterion.status = this.getStatus(completionCriterion);

      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'approve', null, null, null, ignoreAnalytics, eventMetadatum, options));
      } else {
        return resolve(tasksService.approveCompletionCriterion(completionCriterion, eventMetadatum, options).then(response => {
          return this.handleResponse(dontQueue, completionCriterion, response, 'approve', null, null, null, ignoreAnalytics, 'Approving a completion step', eventMetadatum, options);
        }));
      }
    });
  }

  revertCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options) {
    return new Promise((resolve) => {
      completionCriterion.not_applicable = null;
      completionCriterion.status = 'not_started';
      completionCriterion.completed_at = null;
      completionCriterion.reviewed_at = null;
      completionCriterion.approved_at = null;
      completionCriterion.form_fields = [];
      completionCriterion.attachments = [];
      this.normalizeItem(completionCriterion, { store: true });
      this.internalACUpdate(completionCriterion);

      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }
      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(completionCriterion, 'revert', null, null, null, ignoreAnalytics, eventMetadatum, options));
      } else {
        return resolve(tasksService.revertCompletionCriterion(completionCriterion, eventMetadatum, options).then(response => {
          return this.handleResponse(dontQueue, completionCriterion, response, 'revert', null, null, null, ignoreAnalytics, 'Reverting a completion step', eventMetadatum, options);
        }));
      }
    });
  }

  /* Util Functions */
  completeOfflineUpdate(queueItem) {
    const eventMetadatum = queueItem.itemInfo.eventMetadatum;
    const options = queueItem.itemInfo.options;
    const ignoreAnalytics = queueItem.itemInfo.ignoreAnalytics;
    const dontQueue = queueItem.itemInfo.dontQueue;
    const completionCriterion = queueItem.itemInfo.ccId ? this.findItem(queueItem.itemInfo.ccId) : queueItem.itemInfo.cc;
    switch (queueItem.operationKey) {
    case 'update':
      return this.updateCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options);
    case 'create':
      return this.createCompletionCriterion(completionCriterion, eventMetadatum, options);
    case 'delete':
      return this.deleteCompletionCriterion(completionCriterion, dontQueue, eventMetadatum, options);
    case 'mark_not_applicable':
      return this.markNotApplicable(completionCriterion, dontQueue, eventMetadatum);
    case 'unmark_not_applicable':
      return this.unmarkNotApplicable(completionCriterion, dontQueue, eventMetadatum);
    case 'complete':
      if (_.isBoolean(queueItem.itemInfo.completion)) {
        completionCriterion.completed_at = null;
      }
      return this.completeCompletionCriterion(completionCriterion, queueItem.itemInfo.completion, ignoreAnalytics, dontQueue, eventMetadatum, options);
    case 'reject':
      return this.rejectCompletionCriterion(completionCriterion, queueItem.itemInfo.reason, ignoreAnalytics, dontQueue, eventMetadatum, options);
    case 'review':
      return this.reviewCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options);
    case 'approve':
      return this.approveCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options);
    case 'revert':
      return this.revertCompletionCriterion(completionCriterion, ignoreAnalytics, dontQueue, eventMetadatum, options);
              
    default:
      break;
    }
    return true;
  }

  /**
   * Resolves location_id from ActionCable data by checking available IDs in order of directness.
   * Returns null if location cannot be determined (prevents unnecessary collection refreshes).
   */
  _resolveLocationIdFromActionCable(acData) {
    if (!acData) return null;

    // 1. Direct location_id (most direct)
    if (acData.location_id != null) {
      return acData.location_id;
    }

    // 2. Via project_id (ActionCable often includes this)
    if (acData.project_id != null) {
      const project = dataModel.projectsDAO.findItem(acData.project_id);
      if (project && project.location_id != null) {
        return project.location_id;
      }
    }

    // 3. Via task_id (ActionCable includes this for step updates - check BEFORE step lookup)
    if (acData.task_id != null) {
      const task = dataModel.tasksDAO.findItem(acData.task_id);
      if (task && task.project_id != null) {
        const project = dataModel.projectsDAO.findItem(task.project_id);
        if (project && project.location_id != null) {
          return project.location_id;
        }
      }
    }

    // 4. Via step_id (fallback - requires step to be in store)
    if (acData.step_id != null) {
      const step = this.findItem(acData.step_id);
      if (step && step.task_id != null) {
        const task = dataModel.tasksDAO.findItem(step.task_id);
        if (task && task.project_id != null) {
          const project = dataModel.projectsDAO.findItem(task.project_id);
          if (project && project.location_id != null) {
            return project.location_id;
          }
        }
      }
    }

    return null;
  }

  /**
   * Checks if a collection key belongs to a specific location.
   * Key format: LOCATION_PROJECTS_NAV_companyId_userId_locationId_forProjects
   * We check for '_locationId_' pattern which appears between userId and 'forProjects'.
   */
  _collectionKeyMatchesLocation(key, locationId) {
    // The subKey format is 'locationId_forProjects', so we look for '_locationId_'
    // This ensures we don't match partial IDs (e.g., '_12_' won't match '_123_')
    return key.includes('_' + locationId + '_');
  }

  checkPORCollections(acData) {
    // Only mark POR collections as needing sync if we can determine which location
    // they belong to. This prevents the flickering issue where ALL POR collections
    // would refresh when ANY completion step changes anywhere in the system.
    const locationId = this._resolveLocationIdFromActionCable(acData);

    // If we couldn't determine the location, skip marking any collections.
    // This is intentional - it's better to skip an update than refresh all collections.
    // The user can manually refresh if needed.
    if (locationId == null) {
      return;
    }

    const collections = dataModel.collectionsDAO.getCollections();
    if (!collections) return;

    for (const key in collections) {
      if (!key) continue;
      
      const collectionData = collections[key];
      if (!collectionData || collectionData.name !== ReduxConstants.LOCATION_PROJECTS_NAV) {
        continue;
      }

      // Only mark collections that match the location of the changed step
      if (this._collectionKeyMatchesLocation(key, locationId)) {
        collectionData._recordState = RecordStates.NEEDS_SYNC;
        dataModel.collectionsDAO.setRawCollection(key, collectionData);
      }
    }
  }

  handleActionCableAndGetRequest(acData) {
    const options = {
      recordDescriptor: {
        completion_criteria: {
          attachments: { additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'] },
        },
      },
    };

    // Mark documents for POR as needing sync (only for the relevant location)
    this.checkPORCollections(acData);

    const step = this.findItem(acData.step_id);
    if (step) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(step);
      }
      else {
        this.markNeedsSync(step);
      }
    }
    const task = dataModel.tasksDAO.findItem(acData.task_id);
    if (task) {
      if (acData.is_attribute_update) {
        dataModel.tasksDAO.markNeedsAttributeSync(task);
      }
      else {
        dataModel.tasksDAO.markNeedsSync(task);
      }
    }
    if (!acData.is_attribute_update) {
      options.recordDescriptor = completion_criteria_root;
    }
    switch (acData.type) {
    case 'newStep':
      return this.getCompletionCriterion.bind(this);
    case 'updateStep':
      return this.getCompletionCriterion.bind(this);
    case 'deleteStep':
        // TODO - how to handle deletes?
        // return this.deleteCompletionCriterionInStore.bind(this, acData.cc_id)
      break;
    default:
    }
  }

  checkForPendingAttachments(itemType, item) {
    // Need to check what's in the store and ensure that any 
    // pending uploads are also added to the response (lest they
    // disappear from the UI)
    const cc = this.findItem(item.id);
    
    if (cc && cc.attachments && item.attachments) {
      let hasPendingAttachments = false;
      for (const id of cc.attachments) {
        if (id < 0 && !item.attachments.includes(id)) {
          item.attachments.push(id);
          hasPendingAttachments = true;
        }
      }
      if (hasPendingAttachments) {
        // make sure they're sorted by created_at
        const sortedAttachmentIds = [...item.attachments]
          .map(attachmentId => this.findItem(attachmentId, 'attachment'))
          .filter(attachment => !!attachment)
          .sort((a1, a2) => (new Date(a1.created_at) - new Date(a2.created_at)))
          .map(attachment => attachment.id);
        item.attachments = sortedAttachmentIds;
      }
    }
    return item;
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_completion_criteria, options.toIgnore.completionCriteria, this.unsetCompletionCriterion, this.clearCompletionCriteria, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/CompletionCriteriaDAO.js